import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../AppContext';

const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes)) return null;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const detectBrowser = (ua) => {
    if (/SamsungBrowser/i.test(ua)) return 'Samsung Internet';
    if (/Silk\//i.test(ua)) return 'Amazon Silk';
    if (/Edg\//i.test(ua)) return 'Microsoft Edge';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Chrome\//i.test(ua)) return 'Chrome / Chromium';
    if (/Safari\//i.test(ua)) return 'Safari';
    return null;
};

const detectPlatform = (ua) => {
    if (/Tizen/i.test(ua)) return 'Samsung Tizen';
    if (/Web0S|WebOS|NetCast/i.test(ua)) return 'LG webOS';
    if (/VIDAA/i.test(ua)) return 'VIDAA';
    if (/AFT[A-Z0-9]+|Fire TV/i.test(ua)) return 'Amazon Fire TV';
    if (/Android TV|BRAVIA|MiTV|SHIELD Android TV/i.test(ua)) return 'Android TV';
    if (/Android/i.test(ua)) return 'Android';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return navigator.platform || null;
};

const detectDeviceType = (ua) => {
    if (/SmartTV|SMART-TV|HbbTV|Tizen|Web0S|WebOS|NetCast|VIDAA|AFT[A-Z0-9]+|Fire TV|Android TV|BRAVIA|Viera|PhilipsTV|CrKey/i.test(ua)) return 'tv';
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
    return 'computer';
};

const detectDeviceName = (ua, type) => {
    if (/Samsung|Tizen/i.test(ua)) return 'Samsung TV';
    if (/LG|Web0S|WebOS|NetCast/i.test(ua)) return 'LG TV';
    if (/AFT[A-Z0-9]+|Fire TV|Silk/i.test(ua)) return 'Amazon Fire TV';
    if (/BRAVIA/i.test(ua)) return 'Sony Bravia';
    if (/VIDAA/i.test(ua)) return 'VIDAA TV';
    if (/PhilipsTV/i.test(ua)) return 'Philips TV';
    return type === 'tv' ? null : navigator.userAgentData?.platform || navigator.platform || null;
};

export default function DeviceInfo() {
    const { t } = useContext(AppContext);
    const ua = navigator.userAgent || '';
    const deviceType = detectDeviceType(ua);
    const [model, setModel] = useState(() => detectDeviceName(ua, deviceType));
    const [viewport, setViewport] = useState(() => `${window.innerWidth} × ${window.innerHeight}`);
    const [appMemory, setAppMemory] = useState(null);
    const [storage, setStorage] = useState(null);

    useEffect(() => {
        let active = true;

        const updateViewport = () => setViewport(`${window.innerWidth} × ${window.innerHeight}`);
        const updateAppMemory = () => {
            const memory = window.performance?.memory;
            setAppMemory(memory ? {
                used: formatBytes(memory.usedJSHeapSize),
                limit: formatBytes(memory.jsHeapSizeLimit)
            } : null);
        };

        const loadOptionalDetails = async () => {
            try {
                const highEntropy = await navigator.userAgentData?.getHighEntropyValues?.(['model', 'platform', 'platformVersion']);
                if (active && highEntropy?.model) setModel(highEntropy.model);
            } catch (error) {
                // Some TV browsers expose the API but deny high-entropy values.
            }

            try {
                const estimate = await navigator.storage?.estimate?.();
                if (active && estimate) setStorage({
                    used: formatBytes(estimate.usage),
                    quota: formatBytes(estimate.quota)
                });
            } catch (error) {
                // Storage estimates are optional and may be hidden by the browser.
            }
        };

        updateAppMemory();
        loadOptionalDetails();
        const memoryInterval = window.setInterval(updateAppMemory, 5000);
        window.addEventListener('resize', updateViewport);

        return () => {
            active = false;
            window.clearInterval(memoryInterval);
            window.removeEventListener('resize', updateViewport);
        };
    }, []);

    const unavailable = t('device.unavailable');
    const ram = navigator.deviceMemory ? `≈ ${navigator.deviceMemory} GB` : unavailable;
    const cores = navigator.hardwareConcurrency || unavailable;
    const screenSize = `${window.screen.width} × ${window.screen.height}`;
    const connection = navigator.connection?.effectiveType
        ? navigator.connection.effectiveType.toUpperCase()
        : unavailable;
    const cards = [
        { icon: 'TV', label: t('device.device'), value: model || unavailable, meta: t(`device.types.${deviceType}`) },
        { icon: 'OS', label: t('device.system'), value: detectPlatform(ua) || unavailable, meta: detectBrowser(ua) || unavailable },
        { icon: 'RAM', label: t('device.totalMemory'), value: ram, meta: t('device.approximate') },
        { icon: 'APP', label: t('device.appMemory'), value: appMemory?.used || unavailable, meta: appMemory?.limit ? `${t('device.browserLimit')}: ${appMemory.limit}` : t('device.browserRestricted') },
        { icon: 'CPU', label: t('device.cores'), value: cores, meta: t('device.logicalCores') },
        { icon: 'HD', label: t('device.screen'), value: screenSize, meta: `${t('device.viewport')}: ${viewport} · DPR ${window.devicePixelRatio || 1}` },
        { icon: 'NET', label: t('device.connection'), value: connection, meta: navigator.onLine ? t('device.online') : t('device.offline') },
        { icon: 'PWA', label: t('device.appStorage'), value: storage?.used || unavailable, meta: storage?.quota ? `${t('device.availableQuota')}: ${storage.quota}` : t('device.browserRestricted') }
    ];

    return (
        <div className='device-info-panel'>
            <div className='device-info-hero'>
                <span className='device-info-hero-icon' aria-hidden='true'>TV</span>
                <span>
                    <strong>{t('device.title')}</strong>
                    <small>{t('device.localOnly')}</small>
                </span>
            </div>

            <div className='device-info-grid'>
                {cards.map(card => (
                    <div className='device-info-card' key={card.label}>
                        <span className='device-info-icon' aria-hidden='true'>{card.icon}</span>
                        <span className='device-info-copy'>
                            <small>{card.label}</small>
                            <strong>{card.value}</strong>
                            <em>{card.meta}</em>
                        </span>
                    </div>
                ))}
            </div>

            <p className='device-info-note'>{t('device.memoryNote')}</p>
        </div>
    );
}
